import { vendorUpdateSubtypes } from '@intelhub/shared/ontology'
import { Badge } from '@/components/ui/badge'
import { vendorRoutingUiEnabled } from './routing-ui-flag'

/**
 * KAN-822 T9 (reader side) — labels a Vendor story wherever it shows (spec
 * R10, R14). The label follows the STORY's class, never its sources: a
 * mainstream story carrying a vendor source is not relabelled. The subtype
 * name comes from the canonical ontology entry; an unknown slug degrades to the
 * plain label rather than rendering raw data.
 *
 * Brand: classification is a mark (outline badge) with neutral text, never a
 * colour-only signal. No dynamic attributes (replay masking).
 *
 * DARK: renders nothing unless the Vendor UI build flag is on.
 */
export interface VendorLabelledStory {
  distributionClass?: string
  vendorUpdateSubtype?: string | null
}

const SUBTYPE_NAME = new Map(vendorUpdateSubtypes.map((e) => [e.slug, e.name]))

export function isVendorStory(story: VendorLabelledStory): boolean {
  return story.distributionClass === 'vendor'
}

export function VendorBadge({ story }: { story: VendorLabelledStory }) {
  if (!vendorRoutingUiEnabled() || !isVendorStory(story)) return null
  const subtype = story.vendorUpdateSubtype ? SUBTYPE_NAME.get(story.vendorUpdateSubtype) : undefined
  return (
    <Badge variant="outline" className="font-medium text-foreground">
      {subtype ? `Vendor update · ${subtype}` : 'Vendor update'}
    </Badge>
  )
}

/** The one-line explanation on a Vendor story's detail page. */
export function VendorStoryNote({ story }: { story: VendorLabelledStory }) {
  if (!vendorRoutingUiEnabled() || !isVendorStory(story)) return null
  return (
    <p className="text-sm text-muted-foreground">
      Vendor update: commercial news about a vendor, kept apart from threat intelligence.
    </p>
  )
}
